import { useQuery } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import type { Activity } from '../../types/activity';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import {
  getActivityRelKey,
  getCaseClientRelKey,
  getCaseEmailRelKey,
  getCaseFeedRelKey,
  getCaseHistoryRelKey,
  getEmailMessageClientRelKey,
  getEventCaseRelKey,
  getEventClientRelKey,
  getNoteCaseRelKey,
  getNoteClientRelKey,
  getOwnerRelKey,
  getTaskCaseRelKey,
  getTaskClientRelKey,
} from '../../config/gobodoAccountRelationshipKeys';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { convertFromGobodoRecord } from '../gobodo/gobodoRecordService';
import {
  findGraphUserByObjectId,
  findGraphUserByOwnerRelation,
  looksLikeGraphUserSfId,
  resolveGraphUserNamesBySfIds,
} from '../gobodo/gobodoUserSfIdLookup';
import { activityStorage } from '../activityStorage';
import { getRecordsForObject, getRecordsForObjectPage } from '../recordStorageService';
import { toClientGobodoObjectKey } from './creditBlockCaseQueries';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CASE_OBJECT = `Case${CUSTOM_SUFFIX}`;
const TASK_OBJECT = `Task${CUSTOM_SUFFIX}`;
const EVENT_OBJECT = `Event${CUSTOM_SUFFIX}`;
const EMAIL_MESSAGE_OBJECT = `EmailMessage${CUSTOM_SUFFIX}`;
const CASE_HISTORY_OBJECT = `CaseHistory${CUSTOM_SUFFIX}`;
const CASE_FEED_OBJECT = `CaseFeed${CUSTOM_SUFFIX}`;
const NOTE_OBJECT = `Note${CUSTOM_SUFFIX}`;
const ACTIVITY_OBJECT = `Activity${CUSTOM_SUFFIX}`;
const ACTIVITY_FETCH_SIZE = 5000;
const ACTIVITY_FETCH_PAGE_SIZE = 1000;
const ACTIVITY_FETCH_MAX_PAGES = 30;
const OWNER_REL_FIELD_KEY = `OwnerRelField${CUSTOM_SUFFIX}`;

export type CreditBlockActivityItem = Activity & {
  key?: string;
  status?: string;
  priority?: string;
  title?: string;
  objectType?: 'task' | 'event' | 'email' | 'history' | 'note' | 'feed' | 'activity';
  location?: string;
  rawRecord?: Record<string, unknown>;
  ownerSfId?: string;
  creatorObjectId?: string;
};

type TraversalConfig = {
  startObjectKeys: readonly string[];
  relationshipTypes: readonly string[];
  endObjectTypes: readonly string[];
  maxPathLength: number;
  cacheKeyPrefix: string;
};

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function stripKnownSuffix(value: string): string {
  const raw = cleanString(value);
  if (!raw) return '';
  for (const suffix of [CUSTOM_SUFFIX, '__stg3_c', '__stg_c', '__dev_c', '__stg', '__c']) {
    if (suffix && raw.toLowerCase().endsWith(String(suffix).toLowerCase())) {
      return raw.slice(0, raw.length - String(suffix).length);
    }
  }
  return raw;
}

function normalizeForMatch(value: unknown): string {
  return stripKnownSuffix(cleanString(value));
}

function looksLikeUuidObjectKey(raw: string): boolean {
  const value = cleanString(raw);
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function toGraphObjectKey(raw: string): string {
  const s = cleanString(raw);
  if (!s) return '';
  if (/^[0-9]+$/.test(s)) return s;
  if (looksLikeUuidObjectKey(s)) return s;
  if (s.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) return s;
  if (/(?:__stg3_c|__stg_c|__dev_c|__stg|__c)$/i.test(s)) return s;
  return `${s}${CUSTOM_SUFFIX}`;
}

function parseNumericObjectId(raw: string): number | null {
  const value = cleanString(raw);
  if (!/^[0-9]+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function isSuffixedNumericPseudoKey(raw: string): boolean {
  const value = cleanString(raw);
  if (!value || /^[0-9]+$/.test(value)) return false;
  if (!/(?:__stg3_c|__stg_c|__dev_c|__stg|__c)$/i.test(value)) return false;
  return /^[0-9]+$/.test(stripKnownSuffix(value));
}
function extractLookupishId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object' && 'relatedObject' in value) {
    const related = (value as { relatedObject?: { key?: unknown; id?: unknown } }).relatedObject;
    return cleanString(related?.key ?? related?.id);
  }
  return cleanString(value);
}

function getRecordData(record: Record<string, unknown>): Record<string, unknown> {
  return (record.data as Record<string, unknown>) ?? record;
}

function getFirstRecordValue(record: Record<string, unknown>, candidates: readonly string[]): unknown {
  const data = getRecordData(record);
  for (const candidate of candidates) {
    const dataValue = data[candidate];
    if (dataValue != null && cleanString(dataValue)) return dataValue;
    const recordValue = record[candidate];
    if (recordValue != null && cleanString(recordValue)) return recordValue;
  }
  return undefined;
}

function getFirstRecordText(record: Record<string, unknown>, candidates: readonly string[]): string {
  return cleanString(getFirstRecordValue(record, candidates));
}

function getObjectTypeKey(record: Record<string, unknown>): string {
  return cleanString(
    record.objecttypekey ??
      (record.objecttype as { key?: unknown } | undefined)?.key ??
      (record.objectType as { key?: unknown } | undefined)?.key ??
      record.typekey
  );
}

function objectTypeMatches(typeKey: string, expected: string): boolean {
  return normalizeForMatch(typeKey).toLowerCase() === expected.toLowerCase();
}

function buildActivityBinding(caseId: string, clientId: string): { recordId: string; recordType: 'case' | 'client' } {
  if (caseId) return { recordId: caseId, recordType: 'case' };
  return { recordId: clientId, recordType: 'client' };
}

function extractCaseIdFromCandidates(record: Record<string, unknown>, candidates: string[]): string {
  const data = getRecordData(record);
  for (const field of candidates) {
    const token = extractLookupishId(data[field] ?? record[field]);
    if (token) return token;
  }
  return '';
}

function extractClientIdFromRecord(record: Record<string, unknown>): string {
  const data = getRecordData(record);
  for (const candidate of [
    data.AccountId__c,
    record.AccountId__c,
    data.AccountId,
    record.AccountId,
    data.account_id,
    record.account_id,
    data.ClientId__c,
    record.ClientId__c,
    data.client_id,
    record.client_id,
    data.custom__client,
    record.custom__client,
    data.WhoId__c,
    record.WhoId__c,
    data.WhoId,
    record.WhoId,
    data.related_to_id,
    record.related_to_id,
  ]) {
    const token = extractLookupishId(candidate);
    if (token) return token;
  }
  return '';
}

export function extractTaskRelatedCaseId(task: Record<string, unknown>): string {
  return extractCaseIdFromCandidates(task, [
    `task_related_to${CUSTOM_SUFFIX}`,
    'task_related_to__c',
    'RelatedTo__c',
    'relatedTo',
    'WhatId__c',
    'what_id',
  ]);
}

export function extractEventRelatedCaseId(event: Record<string, unknown>): string {
  return extractCaseIdFromCandidates(event, [
    `event_related_to${CUSTOM_SUFFIX}`,
    'event_related_to__c',
    'WhatId__c',
    'what_id',
  ]);
}

export function extractEmailRelatedCaseId(email: Record<string, unknown>): string {
  return extractCaseIdFromCandidates(email, [
    `email_message_related_to${CUSTOM_SUFFIX}`,
    'email_message_related_to__c',
    'ParentId__c',
    'parent_id',
    'case_id',
  ]);
}

function extractCaseFeedRelatedCaseId(feed: Record<string, unknown>): string {
  return extractCaseIdFromCandidates(feed, [
    `case_feed_related_to${CUSTOM_SUFFIX}`,
    'case_feed_related_to__c',
    'ParentId__c',
    'parent_id',
    'case_id',
  ]);
}

function extractNoteRelatedCaseId(note: Record<string, unknown>): string {
  return extractCaseIdFromCandidates(note, [
    'ParentId__c',
    'parent_id',
    'case_id',
    'WhatId__c',
    'what_id',
  ]);
}

function extractActivityOwnerSfId(record: Record<string, unknown>): string {
  const data = getRecordData(record);
  for (const candidate of [
    data.OwnerId__c,
    record.OwnerId__c,
    data.owner_id,
    record.owner_id,
    data.Owner__c,
    record.Owner__c,
    data.owner,
    record.owner,
    data.CreatedById__c,
    record.CreatedById__c,
    data.created_by_id,
    record.created_by_id,
    data[OWNER_REL_FIELD_KEY],
    record[OWNER_REL_FIELD_KEY],
  ]) {
    const token = extractLookupishId(candidate);
    if (token && looksLikeGraphUserSfId(token)) return token;
  }
  return '';
}

function extractOwnerText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object' && 'relatedObject' in value) {
    const related = (value as { relatedObject?: { label?: unknown; name?: unknown; key?: unknown; id?: unknown } }).relatedObject;
    return cleanString(related?.label ?? related?.name ?? related?.key ?? related?.id);
  }
  if (typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    const first = cleanString(raw.FirstName ?? raw.firstName ?? raw.firstname);
    const last = cleanString(raw.LastName ?? raw.lastName ?? raw.lastname);
    return [first, last].filter(Boolean).join(' ').trim() || cleanString(raw.Name ?? raw.name ?? raw.label);
  }
  return cleanString(value);
}

function extractActivityOwnerDisplay(record: Record<string, unknown>): string {
  const data = getRecordData(record);
  for (const candidate of [
    data.OwnerName__c,
    record.OwnerName__c,
    data.owner_name,
    record.owner_name,
    data.CreatedByName__c,
    record.CreatedByName__c,
    data.created_by_name,
    record.created_by_name,
    data.Owner__c,
    record.Owner__c,
    data.owner,
    record.owner,
  ]) {
    const text = extractOwnerText(candidate);
    if (text && !looksLikeGraphUserSfId(text)) return text;
  }
  return '';
}

function normalizeTimestamp(value: unknown): string {
  const raw = cleanString(value);
  if (!raw) return '';
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? raw : dt.toISOString();
}

function normalizeCreditBlockRecordType(value: unknown): 'case' | 'client' {
  return cleanString(value).toLowerCase() === 'case' ? 'case' : 'client';
}

function extractPositiveCreatorObjectId(record: Record<string, unknown>): string {
  const raw = cleanString(
    (record as { creatorid?: unknown }).creatorid ??
      getFirstRecordValue(record, ['createdBy', 'CreatedBy', 'creatorId', 'CreatorId'])
  );
  return /^\d+$/.test(raw) && Number(raw) > 0 ? raw : '';
}

function mapTaskRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const binding = buildActivityBinding(extractTaskRelatedCaseId(record), extractClientIdFromRecord(record));
  const createdAt = normalizeTimestamp(
    data.CreatedDate__c ?? record.CreatedDate__c ?? data.createdDate ?? record.createdDate ?? data.ActivityDateTime__c ?? data.ActivityDate__c
  ) || new Date(0).toISOString();
  const scheduledAt = normalizeTimestamp(
    data.ActivityDateTime__c ?? record.ActivityDateTime__c ?? data.ActivityDate__c ?? record.ActivityDate__c
  );
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'task',
    objectType: 'task',
    title: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    subject: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    description: cleanString(data.Description__c ?? record.Description__c ?? data.description ?? record.description),
    status: cleanString(data.Status__c ?? record.Status__c ?? data.status ?? record.status),
    priority: cleanString(data.Priority__c ?? record.Priority__c ?? data.priority ?? record.priority),
    recordId: binding.recordId,
    recordType: binding.recordType,
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    ...(scheduledAt ? { scheduledAt } : {}),
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapEventRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const binding = buildActivityBinding(extractEventRelatedCaseId(record), extractClientIdFromRecord(record));
  const createdAt = normalizeTimestamp(
    data.CreatedDate__c ??
      record.CreatedDate__c ??
      data.createdDate ??
      record.createdDate ??
      data.StartDateTime__c ??
      data.ActivityDateTime__c ??
      data.ActivityDate__c
  ) || new Date(0).toISOString();
  const scheduledAt = normalizeTimestamp(
    data.StartDateTime__c ??
      record.StartDateTime__c ??
      data.ActivityDateTime__c ??
      record.ActivityDateTime__c ??
      data.ActivityDate__c ??
      record.ActivityDate__c
  );
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'meeting',
    objectType: 'event',
    title: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    subject: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    description: cleanString(data.Description__c ?? record.Description__c ?? data.description ?? record.description),
    status: cleanString(data.Status__c ?? record.Status__c ?? data.status ?? record.status),
    location: cleanString(data.Location__c ?? record.Location__c ?? data.location ?? record.location),
    recordId: binding.recordId,
    recordType: binding.recordType,
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    ...(scheduledAt ? { scheduledAt } : {}),
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapEmailRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const binding = buildActivityBinding(extractEmailRelatedCaseId(record), extractClientIdFromRecord(record));
  const createdAt = normalizeTimestamp(
    data.MessageDate__c ??
      record.MessageDate__c ??
      data.CreatedDate__c ??
      record.CreatedDate__c ??
      data.createdDate ??
      record.createdDate
  ) || new Date(0).toISOString();
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'email',
    objectType: 'email',
    title: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    subject: cleanString(data.Subject__c ?? record.Subject__c ?? data.subject ?? record.subject),
    description: cleanString(data.TextBody__c ?? record.TextBody__c ?? data.Body__c ?? record.Body__c ?? data.description ?? record.description),
    status: cleanString(data.Status__c ?? record.Status__c ?? data.status ?? record.status),
    recordId: binding.recordId,
    recordType: binding.recordType,
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapHistoryRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const createdAt = normalizeTimestamp(
    data.CreatedDate__c ?? record.CreatedDate__c ?? data.createdDate ?? record.createdDate
  ) || new Date(0).toISOString();
  const oldValue = cleanString(data.OldValue__c ?? record.OldValue__c ?? data.old_value ?? record.old_value);
  const newValue = cleanString(data.NewValue__c ?? record.NewValue__c ?? data.new_value ?? record.new_value);
  const description = [oldValue ? `From: ${oldValue}` : '', newValue ? `To: ${newValue}` : '']
    .filter(Boolean)
    .join('\n');
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'note',
    objectType: 'history',
    title: cleanString(data.Field__c ?? record.Field__c ?? data.field ?? record.field),
    subject: cleanString(data.Field__c ?? record.Field__c ?? data.field ?? record.field),
    description,
    recordId: extractCaseIdFromCandidates(record, [
      `case_history_related_to${CUSTOM_SUFFIX}`,
      'case_history_related_to__c',
      'ParentId__c',
      'parent_id',
      'case_id',
    ]),
    recordType: 'case',
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapNoteRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const binding = buildActivityBinding(extractNoteRelatedCaseId(record), extractClientIdFromRecord(record));
  const createdAt = normalizeTimestamp(
    data.CreatedDate__c ?? record.CreatedDate__c ?? data.LastModifiedDate__c ?? record.LastModifiedDate__c ?? data.createdDate ?? record.createdDate
  ) || new Date(0).toISOString();
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'note',
    objectType: 'note',
    title: cleanString(data.Title__c ?? record.Title__c ?? data.Subject__c ?? record.Subject__c ?? data.Name__c ?? record.Name__c ?? data.name ?? record.name),
    subject: cleanString(data.Title__c ?? record.Title__c ?? data.Subject__c ?? record.Subject__c ?? data.Name__c ?? record.Name__c ?? data.name ?? record.name),
    description: cleanString(
      data.Body__c ??
        record.Body__c ??
        data.TextPreview__c ??
        record.TextPreview__c ??
        data.Content__c ??
        record.Content__c ??
        data.description ??
        record.description
    ),
    recordId: binding.recordId,
    recordType: binding.recordType,
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapCaseFeedRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const ownerSfId = extractActivityOwnerSfId(record);
  const ownerDisplay = extractActivityOwnerDisplay(record);
  const createdAt = normalizeTimestamp(
    data.CreatedDate__c ?? record.CreatedDate__c ?? data.LastModifiedDate__c ?? record.LastModifiedDate__c ?? data.createdDate ?? record.createdDate
  ) || new Date(0).toISOString();
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: 'note',
    objectType: 'feed',
    title: cleanString(data.Title__c ?? record.Title__c ?? data.Subject__c ?? record.Subject__c ?? data.Name__c ?? record.Name__c),
    subject: cleanString(data.Title__c ?? record.Title__c ?? data.Subject__c ?? record.Subject__c ?? data.Name__c ?? record.Name__c),
    description: cleanString(data.Body__c ?? record.Body__c ?? data.TextBody__c ?? record.TextBody__c ?? data.description ?? record.description),
    status: cleanString(data.Type__c ?? record.Type__c ?? data.FeedType__c ?? record.FeedType__c),
    recordId: extractCaseFeedRelatedCaseId(record),
    recordType: 'case',
    userId: ownerSfId || ownerDisplay || 'system',
    userName: ownerDisplay || ownerSfId || 'System',
    createdAt,
    source: 'manual',
    sourceApp: 'credit_block',
    ...(ownerSfId ? { ownerSfId } : {}),
    rawRecord: record,
  };
}

function mapGenericActivityRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem {
  const data = getRecordData(record);
  const creatorObjectId = extractPositiveCreatorObjectId(record);
  const userId = getFirstRecordText(record, [
    `UserId${CUSTOM_SUFFIX}`,
    'UserId__c',
    'UserId',
    'user_id',
  ]);
  const userName = getFirstRecordText(record, [
    `UserName${CUSTOM_SUFFIX}`,
    'UserName__c',
    'UserName',
    'user_name',
  ]);
  const createdAt = normalizeTimestamp(
    getFirstRecordValue(record, [
      `CreatedAt${CUSTOM_SUFFIX}`,
      'CreatedAt__c',
      'CreatedAt',
      'createdAt',
      'CreatedDate__c',
      'createdDate',
    ])
  ) || new Date(0).toISOString();
  const scheduledAt = normalizeTimestamp(
    getFirstRecordValue(record, [
      `ScheduledAt${CUSTOM_SUFFIX}`,
      'ScheduledAt__c',
      'ScheduledAt',
      'scheduledAt',
    ])
  );
  const subject = getFirstRecordText(record, [
    `Subject${CUSTOM_SUFFIX}`,
    'Subject__c',
    'Subject',
    'subject',
    'label',
    'name',
  ]);
  const description = getFirstRecordText(record, [
    `Description${CUSTOM_SUFFIX}`,
    'Description__c',
    'Description',
    'description',
  ]);
  const rawType = getFirstRecordText(record, [
    `Type${CUSTOM_SUFFIX}`,
    'Type__c',
    'Type',
    'type',
  ]);
  const recordId = extractLookupishId(
    getFirstRecordValue(record, [
      `RecordId${CUSTOM_SUFFIX}`,
      'RecordId__c',
      'RecordId',
      'record_id',
    ])
  );
  return {
    id: cleanString(record.id ?? data.id ?? record.key ?? data.key),
    key: cleanString(record.key ?? data.key),
    type: (rawType || 'note') as Activity['type'],
    objectType: 'activity',
    title: subject,
    subject,
    description,
    recordId,
    recordType: normalizeCreditBlockRecordType(
      getFirstRecordValue(record, [
        `RecordType${CUSTOM_SUFFIX}`,
        'RecordType__c',
        'RecordType',
        'record_type',
      ])
    ),
    userId: userId || userName || 'system',
    userName: userName || userId || 'System',
    createdAt,
    ...(scheduledAt ? { scheduledAt } : {}),
    source: (getFirstRecordText(record, [
      `Source${CUSTOM_SUFFIX}`,
      'Source__c',
      'Source',
      'source',
    ]) || 'manual') as Activity['source'],
    sourceApp: (getFirstRecordText(record, [
      `SourceApp${CUSTOM_SUFFIX}`,
      'SourceApp__c',
      'SourceApp',
      'sourceApp',
    ]) || 'credit_block') as Activity['sourceApp'],
    ...(looksLikeGraphUserSfId(userId) ? { ownerSfId: userId } : {}),
    ...(creatorObjectId ? { creatorObjectId } : {}),
    rawRecord: record,
  };
}

function toCreditBlockActivityItem(activity: Activity): CreditBlockActivityItem {
  return { ...activity };
}

function mapRecordToActivity(record: Record<string, unknown>): CreditBlockActivityItem | null {
  const objectTypeKey = getObjectTypeKey(record);
  const converted = convertFromGobodoRecord(record, objectTypeKey) as Record<string, unknown>;
  if (objectTypeMatches(objectTypeKey, 'Task')) return mapTaskRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'Event')) return mapEventRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'EmailMessage')) return mapEmailRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'CaseHistory')) return mapHistoryRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'CaseFeed')) return mapCaseFeedRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'Note')) return mapNoteRecordToActivity(converted);
  if (objectTypeMatches(objectTypeKey, 'Activity')) return mapGenericActivityRecordToActivity(converted);
  return null;
}

function dedupeActivities(activities: readonly CreditBlockActivityItem[]): CreditBlockActivityItem[] {
  const seen = new Set<string>();
  const out: CreditBlockActivityItem[] = [];
  for (const activity of activities) {
    const key = [
      cleanString(activity.id),
      cleanString(activity.key),
      cleanString(activity.objectType),
      cleanString(activity.type),
      cleanString(activity.createdAt),
    ].join('|');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(activity);
  }
  return out;
}

function sortActivitiesDesc(activities: readonly CreditBlockActivityItem[]): CreditBlockActivityItem[] {
  return [...activities].sort(
    (a, b) => new Date(b.scheduledAt || b.createdAt).getTime() - new Date(a.scheduledAt || a.createdAt).getTime()
  );
}

async function hydrateCreditBlockActivityOwners(activities: CreditBlockActivityItem[]): Promise<CreditBlockActivityItem[]> {
  if (activities.length === 0) return activities;
  const creatorMatches = await Promise.all(
    activities.map(async (activity) => {
      const creatorObjectId = cleanString(activity.creatorObjectId);
      if (!creatorObjectId) return null;
      return findGraphUserByObjectId(creatorObjectId);
    })
  );
  // Per-activity graph traversal is the most expensive lookup path —
  // findGraphUserByOwnerRelation tries 5+ user-type variants and the cache
  // key is per-activity, so N activities = N×5 traversal calls. Skip
  // entirely if we either:
  //   - already resolved the creator via the (cached) object-id path, OR
  //   - have an ownerSfId on the activity which gets batched at line 713
  //     via resolveGraphUserNamesBySfIds (1 call for all activities)
  const relationMatches = await Promise.all(
    activities.map(async (activity, index) => {
      if (creatorMatches[index]?.displayName) return null;
      if (cleanString(activity.ownerSfId)) return null;
      const startObjectKey = cleanString(activity.key ?? activity.id);
      if (!startObjectKey) return null;
      return findGraphUserByOwnerRelation({
        startObjectKey,
        relationshipType: getOwnerRelKey(),
      });
    })
  );
  const ownerSfIds = Array.from(
    new Set(
      activities
        .map((activity, index) => (relationMatches[index]?.displayName ? '' : cleanString(activity.ownerSfId)))
        .filter(Boolean)
    )
  );
  const namesBySfId = ownerSfIds.length > 0 ? await resolveGraphUserNamesBySfIds(ownerSfIds) : new Map<string, string>();
  return activities.map((activity, index) => {
    const creatorMatch = creatorMatches[index];
    if (creatorMatch?.displayName) {
      return {
        ...activity,
        userName: creatorMatch.displayName,
        userId: creatorMatch.sfId || creatorMatch.id || activity.userId,
        ownerSfId: creatorMatch.sfId || activity.ownerSfId,
      };
    }
    const relationMatch = relationMatches[index];
    if (relationMatch?.displayName) {
      return {
        ...activity,
        userName: relationMatch.displayName,
        userId: relationMatch.sfId || activity.userId,
        ownerSfId: relationMatch.sfId || activity.ownerSfId,
      };
    }
    const ownerSfId = cleanString(activity.ownerSfId);
    const fallback = ownerSfId ? namesBySfId.get(ownerSfId) || namesBySfId.get(stripKnownSuffix(ownerSfId)) : '';
    return fallback ? { ...activity, userName: fallback, userId: ownerSfId || activity.userId } : activity;
  });
}

async function traverseActivityObjects(config: TraversalConfig): Promise<Record<string, unknown>[]> {
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  for (const startObjectKey of config.startObjectKeys) {
    const key = cleanString(startObjectKey);
    if (!key) continue;
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    const numericId = parseNumericObjectId(key);
    if (numericId != null) {
      input.setStartObjectId(numericId);
    } else {
      input.setStartObjectKey(key);
    }
    input.setRelationshipTypes([...config.relationshipTypes]);
    input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
    input.setEndObjectTypes([...config.endObjectTypes]);
    input.setMaxPathLength(config.maxPathLength);
    const result = await retryWithBackoff(
      () =>
        traversalApi.traverseObjects(input, {
          cachekey: [config.cacheKeyPrefix, key],
          cacheduration: '90',
        }),
      { maxRetries: 2, initialDelayMs: 750 }
    );
    if (Array.isArray(result) && result.length > 0) {
      return result as Record<string, unknown>[];
    }
  }
  return [];
}

async function loadAllRecordsForObject(objectApiName: string): Promise<Record<string, unknown>[]> {
  const firstPage = await getRecordsForObjectPage(objectApiName, { page: 0, size: ACTIVITY_FETCH_PAGE_SIZE });
  const out = [...firstPage.records];
  const total = Number(firstPage.totalRecords ?? 0);
  if (total <= out.length || out.length < ACTIVITY_FETCH_PAGE_SIZE) {
    return out as Record<string, unknown>[];
  }
  const pages = Math.min(ACTIVITY_FETCH_MAX_PAGES, Math.ceil(total / ACTIVITY_FETCH_PAGE_SIZE));
  for (let page = 1; page < pages; page++) {
    const next = await getRecordsForObjectPage(objectApiName, { page, size: ACTIVITY_FETCH_PAGE_SIZE });
    if (!Array.isArray(next.records) || next.records.length === 0) break;
    out.push(...next.records);
    if (next.records.length < ACTIVITY_FETCH_PAGE_SIZE) break;
  }
  return out as Record<string, unknown>[];
}

function caseLookupSet(caseLookups: readonly string[]): Set<string> {
  const set = new Set<string>();
  for (const lookup of caseLookups) {
    const raw = cleanString(lookup);
    if (!raw) continue;
    set.add(raw);
    set.add(normalizeForMatch(raw));
  }
  return set;
}

function matchesCaseLookup(caseId: string, knownCaseLookups: Set<string>): boolean {
  const raw = cleanString(caseId);
  if (!raw) return false;
  return knownCaseLookups.has(raw) || knownCaseLookups.has(normalizeForMatch(raw));
}

function getCaseLookupTokens(record: Record<string, unknown>): string[] {
  const data = getRecordData(record);
  return Array.from(
    new Set(
      [
        cleanString(record.key),
        cleanString(record.id),
        cleanString(data.sf_id),
        cleanString(data.CaseId__c),
        cleanString(data.case_id),
      ].filter(Boolean)
    )
  );
}

function extractCaseClientId(record: Record<string, unknown>): string {
  const data = getRecordData(record);
  for (const candidate of [
    data.AccountId__c,
    record.AccountId__c,
    data.AccountRelField__c,
    record.AccountRelField__c,
    data.account_id,
    record.account_id,
    data.accountId,
    record.accountId,
    data.Client__c,
    record.Client__c,
    data.ClientRelField__c,
    record.ClientRelField__c,
    data.custom__client,
    record.custom__client,
  ]) {
    const token = extractLookupishId(candidate);
    if (token) return token;
  }
  return '';
}

async function loadActivitiesViaBulkFetch(caseLookups: readonly string[]): Promise<CreditBlockActivityItem[]> {
  const knownCaseLookups = caseLookupSet(caseLookups);
  const [taskRows, eventRows, emailRows, historyRows, feedRows, noteRows] = await Promise.all([
    loadAllRecordsForObject(TASK_OBJECT).catch(() => getRecordsForObject(TASK_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
    loadAllRecordsForObject(EVENT_OBJECT).catch(() => getRecordsForObject(EVENT_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
    loadAllRecordsForObject(EMAIL_MESSAGE_OBJECT).catch(() => getRecordsForObject(EMAIL_MESSAGE_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
    loadAllRecordsForObject(CASE_HISTORY_OBJECT).catch(() => getRecordsForObject(CASE_HISTORY_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
    loadAllRecordsForObject(CASE_FEED_OBJECT).catch(() => getRecordsForObject(CASE_FEED_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
    loadAllRecordsForObject(NOTE_OBJECT).catch(() => getRecordsForObject(NOTE_OBJECT, { size: ACTIVITY_FETCH_SIZE })),
  ]);
  return [
    ...taskRows
      .filter((row) => matchesCaseLookup(extractTaskRelatedCaseId(row as Record<string, unknown>), knownCaseLookups))
      .map((row) => mapTaskRecordToActivity(row as Record<string, unknown>)),
    ...eventRows
      .filter((row) => matchesCaseLookup(extractEventRelatedCaseId(row as Record<string, unknown>), knownCaseLookups))
      .map((row) => mapEventRecordToActivity(row as Record<string, unknown>)),
    ...emailRows
      .filter((row) => matchesCaseLookup(extractEmailRelatedCaseId(row as Record<string, unknown>), knownCaseLookups))
      .map((row) => mapEmailRecordToActivity(row as Record<string, unknown>)),
    ...historyRows
      .filter((row) =>
        matchesCaseLookup(
          extractCaseIdFromCandidates(row as Record<string, unknown>, [
            `case_history_related_to${CUSTOM_SUFFIX}`,
            'case_history_related_to__c',
            'ParentId__c',
            'parent_id',
            'case_id',
          ]),
          knownCaseLookups
        )
      )
      .map((row) => mapHistoryRecordToActivity(row as Record<string, unknown>)),
    ...feedRows
      .filter((row) => matchesCaseLookup(extractCaseFeedRelatedCaseId(row as Record<string, unknown>), knownCaseLookups))
      .map((row) => mapCaseFeedRecordToActivity(row as Record<string, unknown>)),
    ...noteRows
      .filter((row) => matchesCaseLookup(extractNoteRelatedCaseId(row as Record<string, unknown>), knownCaseLookups))
      .map((row) => mapNoteRecordToActivity(row as Record<string, unknown>)),
  ];
}

async function loadFallbackCaseActivities(caseLookup: string): Promise<CreditBlockActivityItem[]> {
  const genericActivities = await activityStorage
    .getAll({ recordId: caseLookup, recordType: 'case' })
    .then((rows) => rows.map(toCreditBlockActivityItem))
    .catch(() => [] as CreditBlockActivityItem[]);
  const caseActivities = await loadActivitiesViaBulkFetch([caseLookup]).catch(() => [] as CreditBlockActivityItem[]);
  return sortActivitiesDesc(dedupeActivities([...genericActivities, ...caseActivities]));
}

async function loadFallbackClientActivities(clientLookup: string): Promise<CreditBlockActivityItem[]> {
  const directActivities = await activityStorage
    .getAll({ recordId: clientLookup, recordType: 'client' })
    .then((rows) => rows.map(toCreditBlockActivityItem))
    .catch(() => [] as CreditBlockActivityItem[]);
  const cases = await loadAllRecordsForObject(CASE_OBJECT).catch(() => getRecordsForObject(CASE_OBJECT, { size: ACTIVITY_FETCH_SIZE }) as Promise<Record<string, unknown>[]>);
  const candidates = new Set(
    [
      cleanString(clientLookup),
      stripKnownSuffix(clientLookup),
      toClientGobodoObjectKey(clientLookup),
      toClientGobodoObjectKey(stripKnownSuffix(clientLookup)),
    ]
      .map(normalizeForMatch)
      .filter(Boolean)
  );
  const caseLookups = Array.from(
    new Set(
      (cases as Record<string, unknown>[])
        .filter((row) => {
          const clientId = normalizeForMatch(extractCaseClientId(row));
          return !!clientId && candidates.has(clientId);
        })
        .flatMap((row) => getCaseLookupTokens(row))
        .filter(Boolean)
    )
  );
  const caseActivities = caseLookups.length > 0
    ? await loadActivitiesViaBulkFetch(caseLookups).catch(() => [] as CreditBlockActivityItem[])
    : [];
  return sortActivitiesDesc(dedupeActivities([...directActivities, ...caseActivities]));
}

async function finalizeActivities(activities: CreditBlockActivityItem[]): Promise<CreditBlockActivityItem[]> {
  const deduped = dedupeActivities(activities);
  const hydrated = await hydrateCreditBlockActivityOwners(deduped);
  return sortActivitiesDesc(hydrated);
}

function buildClientActivityStartKeys(clientKey: string): string[] {
  const raw = cleanString(clientKey);
  const stripped = stripKnownSuffix(raw);
  const canonical = cleanString(toClientGobodoObjectKey(raw) || toClientGobodoObjectKey(stripped) || '');
  if (canonical) {
    const canonicalStripped = stripKnownSuffix(canonical);
    return Array.from(
      new Set(
        [
          toGraphObjectKey(canonical),
          toGraphObjectKey(canonicalStripped),
          canonical,
          canonicalStripped,
        ]
          .map((value) => cleanString(value))
          .filter(Boolean)
      )
    );
  }
  return Array.from(
    new Set(
      [
        toGraphObjectKey(raw),
        toGraphObjectKey(stripped),
        toClientGobodoObjectKey(raw),
        toClientGobodoObjectKey(stripped),
        raw,
        stripped,
      ]
        .map((value) => cleanString(value))
        .filter(Boolean)
    )
  );
}

function buildCaseActivityStartKeys(caseKey: string): string[] {
  const raw = cleanString(caseKey);
  const stripped = stripKnownSuffix(raw);
  const preferNumericId = isSuffixedNumericPseudoKey(raw);
  const ordered = preferNumericId
    ? [stripped, raw, toGraphObjectKey(stripped), toGraphObjectKey(raw)]
    : [raw, stripped, toGraphObjectKey(raw), toGraphObjectKey(stripped)];
  return Array.from(
    new Set(
      ordered
        .map((value) => cleanString(value))
        .filter(Boolean)
    )
  );
}

async function getActivitiesViaClientTraversal(clientKey: string): Promise<CreditBlockActivityItem[]> {
  const rows = await traverseActivityObjects({
    startObjectKeys: buildClientActivityStartKeys(clientKey),
    relationshipTypes: [
      getActivityRelKey(),
      getCaseClientRelKey(),
      getTaskClientRelKey(),
      getEventClientRelKey(),
      getEmailMessageClientRelKey(),
      getNoteClientRelKey(),
      getNoteCaseRelKey(),
      getTaskCaseRelKey(),
      getEventCaseRelKey(),
      getCaseEmailRelKey(),
      getCaseHistoryRelKey(),
      getCaseFeedRelKey(),
    ],
    endObjectTypes: [
      ACTIVITY_OBJECT,
      TASK_OBJECT,
      EVENT_OBJECT,
      EMAIL_MESSAGE_OBJECT,
      NOTE_OBJECT,
      CASE_HISTORY_OBJECT,
      CASE_FEED_OBJECT,
    ],
    maxPathLength: 2,
    cacheKeyPrefix: 'credit-block-client-activities',
  });
  return rows
    .map((row) => mapRecordToActivity(row as Record<string, unknown>))
    .filter((row): row is CreditBlockActivityItem => Boolean(row));
}

async function getActivitiesViaCaseTraversal(caseKey: string): Promise<CreditBlockActivityItem[]> {
  const rows = await traverseActivityObjects({
    startObjectKeys: buildCaseActivityStartKeys(caseKey),
    relationshipTypes: [
      getActivityRelKey(),
      getTaskCaseRelKey(),
      getEventCaseRelKey(),
      getCaseEmailRelKey(),
      getCaseHistoryRelKey(),
      getCaseFeedRelKey(),
      getNoteCaseRelKey(),
    ],
    endObjectTypes: [ACTIVITY_OBJECT, TASK_OBJECT, EVENT_OBJECT, EMAIL_MESSAGE_OBJECT, NOTE_OBJECT, CASE_HISTORY_OBJECT, CASE_FEED_OBJECT],
    maxPathLength: 1,
    cacheKeyPrefix: 'credit-block-case-activities',
  });
  return rows
    .map((row) => mapRecordToActivity(row as Record<string, unknown>))
    .filter((row): row is CreditBlockActivityItem => Boolean(row));
}

export function splitCreditBlockActivities(activities: readonly CreditBlockActivityItem[]): {
  openActivities: CreditBlockActivityItem[];
  historyActivities: CreditBlockActivityItem[];
} {
  const now = Date.now();
  const openActivities: CreditBlockActivityItem[] = [];
  const historyActivities: CreditBlockActivityItem[] = [];
  for (const activity of activities) {
    const status = cleanString(activity.status).toLowerCase();
    const scheduledAt = cleanString(activity.scheduledAt || activity.createdAt);
    const scheduledMs = scheduledAt ? new Date(scheduledAt).getTime() : Number.NaN;
    const isTask = activity.objectType === 'task' || activity.type === 'task';
    const isHistory =
      (isTask && ['completed', 'done', 'cancelled', 'closed'].includes(status)) ||
      (!isTask && (['completed', 'done', 'cancelled'].includes(status) || (!Number.isNaN(scheduledMs) && scheduledMs < now)));
    if (isHistory) historyActivities.push(activity);
    else openActivities.push(activity);
  }
  return {
    openActivities: sortActivitiesDesc(openActivities),
    historyActivities: sortActivitiesDesc(historyActivities),
  };
}

export async function getCreditBlockActivitiesForClient(clientKey: string): Promise<CreditBlockActivityItem[]> {
  const raw = cleanString(clientKey);
  if (!raw) return [];
  const canonicalClientKey = cleanString(toClientGobodoObjectKey(raw) || toClientGobodoObjectKey(stripKnownSuffix(raw)) || raw);
  if (!shouldUseGobodo()) {
    return loadFallbackClientActivities(canonicalClientKey);
  }
  const traversed = await getActivitiesViaClientTraversal(canonicalClientKey);
  return finalizeActivities(traversed);
}

export async function getCreditBlockActivitiesForCase(caseKey: string): Promise<CreditBlockActivityItem[]> {
  const raw = cleanString(caseKey);
  if (!raw) return [];
  if (!shouldUseGobodo()) {
    return loadFallbackCaseActivities(raw);
  }
  const traversed = await getActivitiesViaCaseTraversal(raw);
  return finalizeActivities(traversed);
}

export async function getCreditBlockActivitiesForCases(caseLookups: readonly string[]): Promise<CreditBlockActivityItem[]> {
  const cleanLookups = Array.from(new Set(caseLookups.map((value) => cleanString(value)).filter(Boolean)));
  if (cleanLookups.length === 0) return [];
  if (cleanLookups.length === 1) {
    return getCreditBlockActivitiesForCase(cleanLookups[0]);
  }
  const grouped = await Promise.all(cleanLookups.map((caseLookup) => getCreditBlockActivitiesForCase(caseLookup)));
  return finalizeActivities(grouped.flat());
}

export function useCreditBlockActivitiesForClient(clientKey: string, enabled = true) {
  const normalizedKey = cleanString(clientKey);
  return useQuery({
    queryKey: ['credit-block', 'activities', 'client', normalizedKey],
    queryFn: () => getCreditBlockActivitiesForClient(normalizedKey),
    enabled: enabled && !!normalizedKey,
    staleTime: 90 * 1000,
  });
}

export function useCreditBlockActivitiesForCase(caseKey: string, enabled = true) {
  const normalizedKey = cleanString(caseKey);
  return useQuery({
    queryKey: ['credit-block', 'activities', 'case', normalizedKey],
    queryFn: () => getCreditBlockActivitiesForCase(normalizedKey),
    enabled: enabled && !!normalizedKey,
    staleTime: 90 * 1000,
  });
}

export function useCreditBlockActivitiesForCases(caseLookups: readonly string[], enabled = true) {
  const normalizedLookups = Array.from(new Set(caseLookups.map((value) => cleanString(value)).filter(Boolean)));
  return useQuery({
    queryKey: ['credit-block', 'activities', normalizedLookups.join(',')],
    queryFn: () => getCreditBlockActivitiesForCases(normalizedLookups),
    enabled: enabled && normalizedLookups.length > 0,
    staleTime: 90 * 1000,
  });
}
